import React from 'react';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

/* Numeración FDI (WHO) — 32 piezas permanentes, cuadrantes 1-4 */
const CUADRANTES = [1, 2, 3, 4];
const numerosFDI = () => {
  const out = [];
  for (const cuad of CUADRANTES) for (let n = 8; n >= 1; n--) out.push(String(cuad * 10 + n));
  return out;
};

const ESTADOS_PIEZA = [
  { cod: 'SAN', label: 'Sana' },
  { cod: 'AUS', label: 'Ausente' },
  { cod: 'EXT', label: 'Extracción indicada' },
  { cod: 'END', label: 'Endodoncia' },
  { cod: 'COR', label: 'Corona' },
  { cod: 'IMP', label: 'Implante' },
  { cod: 'FRA', label: 'Fractura' },
];

const ESTADOS_SUPERFICIE = [
  { cod: 'SAN', label: 'Sana' },
  { cod: 'CAR', label: 'Caries activa' },
  { cod: 'OBT', label: 'Obturado' },
];

/* rojo = pendiente/problema, azul = tratamiento completado — convención LatAm */
const colorEstadoPieza = cod => ({
  SAN: 'var(--text3)',
  AUS: 'var(--text3)',
  EXT: 'var(--red)',
  FRA: 'var(--red)',
  END: 'var(--blue)',
  COR: 'var(--blue)',
  IMP: 'var(--blue)',
}[cod] || 'var(--text3)');

const colorEstadoSuperficie = cod => ({
  SAN: 'var(--bg)',
  CAR: 'var(--red)',
  OBT: 'var(--blue)',
}[cod] || 'var(--bg)');

/* Icono de diente en SVG (corona + raíz), coloreado según estado whole-tooth */
function DienteSVG({ estado, ausente }) {
  const color = colorEstadoPieza(estado);
  if (ausente || estado === 'AUS') {
    return (
      <svg viewBox="0 0 32 44" width="28" height="38">
        <path d="M16 4 C9 4 5 9 5 15 C5 19 6 22 7 25 L9 25 C9 22 10 20 11 20 C12 20 12.5 23 13 27 L15.5 27 C15.5 22 16 19 16 19 C16 19 16.5 22 16.5 27 L19 27 C19.5 23 20 20 21 20 C22 20 23 22 23 25 L25 25 C26 22 27 19 27 15 C27 9 23 4 16 4 Z"
          fill="none" stroke="var(--border)" strokeWidth="1.3" strokeDasharray="2.5 2.5" opacity="0.55" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 32 44" width="28" height="38">
      <path d="M16 4 C9 4 5 9 5 15 C5 19 6 22 7 25 L9 25 C9 22 10 20 11 20 C12 20 12.5 23 13 27 L15.5 27 C15.5 22 16 19 16 19 C16 19 16.5 22 16.5 27 L19 27 C19.5 23 20 20 21 20 C22 20 23 22 23 25 L25 25 C26 22 27 19 27 15 C27 9 23 4 16 4 Z"
        fill={estado === 'SAN' ? 'var(--surface)' : color}
        fillOpacity={estado === 'SAN' ? 1 : 0.16}
        stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M9 15 C9 11.5 12 8.5 16 8.5 C20 8.5 23 11.5 23 15" fill="none" stroke={color} strokeWidth="1" strokeOpacity="0.35" />
    </svg>
  );
}

export default function Odontograma() {
  const [sp] = useSearchParams();
  const { usuario } = useAuth();
  const [codPac, setCodPac]   = useState(sp.get('paciente') || '');
  const [pacBusq, setPacBusq] = useState('');
  const [pacSugs, setPacSugs] = useState([]);
  const [paciente, setPac]    = useState(null);
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [piezaSel, setPiezaSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loadDet, setLoadDet] = useState(false);
  const [superficieSel, setSuperficieSel] = useState(null);
  const [tab, setTab] = useState('overview'); // 'overview' | 'historial'
  const [historial, setHistorial] = useState([]);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const [formPieza, setFormPieza] = useState({ C_ESTADO_PIEZA: 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
  const [formSup, setFormSup] = useState({ C_ESTADO_SUPERFICIE: 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });

  const [plan, setPlan] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [showAgregarPrestacion, setShowAgregarPrestacion] = useState(false);
  const [descuentoLineaAbierto, setDescuentoLineaAbierto] = useState(false);
  const [nuevaLinea, setNuevaLinea] = useState({ NUMERO_PIEZA: '', C_SUPERFICIE: '', NOMBRE_TRATAMIENTO: '', PRECIO: '', DESCUENTO: 0 });
  const [descuentoEdit, setDescuentoEdit] = useState('');
  const esAdmin = usuario?.esDBA || (usuario?.tipo_usuario || '').toUpperCase().trim() === 'ADMINISTRADOR';

  useEffect(() => {
    if (!pacBusq.trim()) { setPacSugs([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/pacientes?buscar=${encodeURIComponent(pacBusq)}`);
        setPacSugs(data.slice ? data.slice(0, 8) : []);
      } catch (e) { setPacSugs([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [pacBusq]);

  useEffect(() => {
    if (!codPac) return;
    cargarPaciente();
    cargarOverview();
  }, [codPac]);

  const cargarPaciente = async () => {
    try {
      const { data } = await api.get(`/pacientes/${codPac}`);
      setPac(data);
    } catch (e) { /* silencioso */ }
  };

  const cargarOverview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/odontograma/${codPac}`);
      setOverview(data);
    } catch (e) {
      setMsg('✕ No se pudo cargar el odontograma');
    } finally {
      setLoading(false);
    }
  };

  const cargarPlan = async () => {
    try {
      const { data } = await api.get(`/odontograma/plan/${codPac}`);
      setPlan(data);
      setDescuentoEdit(String(data.descuentoComercial));
    } catch (e) {
      setPlan(null);
      const detalle = e.response?.data?.error || e.message || 'sin detalle';
      setMsg('✕ No se pudo cargar el presupuesto (' + detalle + '). Verifica que el backend de odontograma esté actualizado.');
    }
  };

  const cargarCatalogo = async () => {
    try {
      const { data } = await api.get('/odontograma/catalogo/lista');
      setCatalogo(data);
    } catch (e) { setCatalogo([]); }
  };

  useEffect(() => {
    if (codPac) { cargarPlan(); cargarCatalogo(); }
  }, [codPac]);

  const guardarDescuento = async () => {
    if (!plan) { setMsg('✕ El presupuesto todavía no ha cargado — espera un momento o recarga la página'); return; }
    const val = Number(descuentoEdit);
    if (isNaN(val) || val < 0 || val > 100) { setMsg('✕ El descuento debe ser un número entre 0 y 100'); return; }
    try {
      await api.put(`/odontograma/plan/${plan.COD}/descuento`, { DESCUENTO_COMERCIAL: val });
      await cargarPlan();
    } catch (e) {
      setMsg('✕ No se pudo actualizar el descuento: ' + (e.response?.data?.error || e.message || 'sin detalle'));
    }
  };

  const agregarLinea = async () => {
    if (!plan) { setMsg('✕ El presupuesto todavía no ha cargado — espera un momento o recarga la página'); return; }
    if (!nuevaLinea.NUMERO_PIEZA.trim()) { setMsg('✕ Escribe el número de pieza'); return; }
    if (!nuevaLinea.NOMBRE_TRATAMIENTO.trim()) { setMsg('✕ Escribe qué se va a hacer'); return; }
    if (nuevaLinea.PRECIO === '' || isNaN(Number(nuevaLinea.PRECIO)) || Number(nuevaLinea.PRECIO) < 0) {
      setMsg('✕ Escribe un precio válido'); return;
    }
    try {
      const nombreEscrito = nuevaLinea.NOMBRE_TRATAMIENTO.trim();
      let codTratamiento = catalogo.find(c => c.NOMBRE.toLowerCase() === nombreEscrito.toLowerCase())?.COD;

      // Si escribió una prestación nueva que no está en el catálogo, se crea al vuelo
      if (!codTratamiento) {
        const { data } = await api.post('/odontograma/catalogo', {
          NOMBRE: nombreEscrito,
          PRECIO_BASE: Number(nuevaLinea.PRECIO),
        });
        codTratamiento = data.COD;
        await cargarCatalogo();
      }

      await api.post(`/odontograma/plan/${plan.COD}/lineas`, {
        NUMERO_PIEZA: nuevaLinea.NUMERO_PIEZA,
        C_SUPERFICIE: nuevaLinea.C_SUPERFICIE || null,
        COD_TRATAMIENTO: codTratamiento,
        PRECIO: Number(nuevaLinea.PRECIO),
        DESCUENTO: Number(nuevaLinea.DESCUENTO) || 0,
      });
      setMsg('✓ Prestación agregada al plan');
      setNuevaLinea({ NUMERO_PIEZA: '', C_SUPERFICIE: '', NOMBRE_TRATAMIENTO: '', PRECIO: '', DESCUENTO: 0 });
      setDescuentoLineaAbierto(false);
      setShowAgregarPrestacion(false);
      await cargarPlan();
    } catch (e) {
      const detalle = e.response?.data?.error || e.message || 'sin detalle del servidor';
      setMsg('✕ No se pudo agregar la prestación: ' + detalle);
    }
  };

  const quitarLinea = async codLinea => {
    try {
      await api.delete(`/odontograma/plan/lineas/${codLinea}`);
      await cargarPlan();
    } catch (e) {
      setMsg('✕ No se pudo quitar la prestación: ' + (e.response?.data?.error || e.message || 'sin detalle'));
    }
  };

  const abrirPieza = async numeroPieza => {
    setPiezaSel(numeroPieza);
    setSuperficieSel(null);
    setLoadDet(true);
    try {
      const { data } = await api.get(`/odontograma/pieza/${codPac}/${numeroPieza}`);
      setDetalle(data);
      setFormPieza({ C_ESTADO_PIEZA: data.estado || 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
    } catch (e) {
      setMsg('✕ No se pudo cargar la pieza');
    } finally {
      setLoadDet(false);
    }
  };

  const guardarPieza = async () => {
    if (!formPieza.TRATAMIENTO.trim()) { setMsg('✕ Describe el tratamiento realizado'); return; }
    setSaving(true); setMsg('');
    try {
      await api.post('/odontograma/tratamiento', {
        COD_PACIENTE: codPac, NUMERO_PIEZA: piezaSel,
        C_ESTADO_PIEZA: formPieza.C_ESTADO_PIEZA,
        TRATAMIENTO: formPieza.TRATAMIENTO,
        OBSERVACIONES: formPieza.OBSERVACIONES || null,
      });
      setMsg('✓ Pieza actualizada');
      await cargarOverview();
      await abrirPieza(piezaSel);
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const abrirSuperficie = superficie => {
    setSuperficieSel(superficie);
    const actual = detalle?.superficies?.find(s => s.superficie === superficie);
    setFormSup({ C_ESTADO_SUPERFICIE: actual?.estado || 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
  };

  const guardarSuperficie = async () => {
    if (!formSup.TRATAMIENTO.trim()) { setMsg('✕ Describe el tratamiento realizado'); return; }
    setSaving(true); setMsg('');
    try {
      await api.post('/odontograma/superficie', {
        COD_PACIENTE: codPac, NUMERO_PIEZA: piezaSel, C_SUPERFICIE: superficieSel,
        C_ESTADO_SUPERFICIE: formSup.C_ESTADO_SUPERFICIE,
        TRATAMIENTO: formSup.TRATAMIENTO,
        OBSERVACIONES: formSup.OBSERVACIONES || null,
      });
      setMsg('✓ Superficie actualizada');
      await cargarOverview();
      await abrirPieza(piezaSel);
      setSuperficieSel(null);
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const cargarHistorial = async () => {
    try {
      const { data } = await api.get(`/odontograma/tratamientos/${codPac}`);
      setHistorial(data);
    } catch (e) { setHistorial([]); }
  };

  useEffect(() => {
    if (tab === 'historial' && codPac) cargarHistorial();
  }, [tab, codPac]);

  const hallazgoDe = numero => overview.find(o => o.numeroPieza === numero);

  /* ═══ SIN PACIENTE ═══ */
  if (!codPac) {
    return (
      <>
        <style>{css}</style>
        <div className="od-search-wrap">
          <h2 className="od-search-title">Odontograma — buscar paciente</h2>
          <input className="od-search-input" placeholder="Buscar por nombre o cédula..."
            value={pacBusq} onChange={e => setPacBusq(e.target.value)} autoFocus />
          {pacSugs.length > 0 && (
            <div className="od-search-sugs">
              {pacSugs.map(p => (
                <div key={p.COD} className="od-search-sug"
                  onClick={() => { setCodPac(String(p.COD)); setPacBusq(''); setPacSugs([]); }}>
                  <div className="od-search-sug-nom">{[p.APELLIDO_PATERNO, p.APELLIDO_MATERNO, p.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
                  <div className="od-search-sug-ci">CI: {p.IDENTIFICACION || '—'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }

  /* ═══ CON PACIENTE ═══ */
  return (
    <>
      <style>{css}</style>
      {msg && <div className={`od-msg ${msg.startsWith('✓') ? 'ok' : 'err'}`}>{msg}</div>}

      <div className="od-page">
        <div className="od-hdr">
          <div>
            <div className="od-hdr-name">{[paciente?.APELLIDO_PATERNO, paciente?.APELLIDO_MATERNO, paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
            <div className="od-hdr-sub">CI: {paciente?.IDENTIFICACION || '—'}</div>
          </div>
          <button className="btn-od-sec" onClick={() => { setCodPac(''); setPac(null); setOverview([]); setDetalle(null); setPiezaSel(null); }}>
            Cambiar paciente
          </button>
        </div>

        <div className="od-tabs">
          <div className={`od-tab ${tab === 'overview' ? 'act' : ''}`} onClick={() => setTab('overview')}>
            <i className="ti ti-dental" aria-hidden="true"></i> Odontograma
          </div>
          <div className={`od-tab ${tab === 'historial' ? 'act' : ''}`} onClick={() => setTab('historial')}>
            <i className="ti ti-history" aria-hidden="true"></i> Historial
          </div>
        </div>

        {tab === 'overview' && (
          <div className="od-body od-body-conpresupuesto">
            <div className="od-presupuesto-col">
              <div className="od-presupuesto">
                <div className="od-presupuesto-hdr">Presupuesto total</div>
                <div className="od-presupuesto-total">${plan?.total?.toFixed(2) ?? '0.00'}</div>

                <div className="od-presupuesto-desc">
                  <span>Descuento comercial</span>
                  {esAdmin ? (
                    <div className="od-presupuesto-desc-edit">
                      <input type="number" min="0" max="100" value={descuentoEdit}
                        onChange={e => setDescuentoEdit(e.target.value)} />
                      <span>%</span>
                      <button className="btn-mo-sec" style={{ padding: '3px 8px', fontSize: 11 }} onClick={guardarDescuento}>✓</button>
                    </div>
                  ) : (
                    <span>{plan?.descuentoComercial ?? 0}%</span>
                  )}
                </div>

                {plan?.lineas?.length > 0 && (
                  <div className="od-presupuesto-lineas">
                    {plan.lineas.map(l => (
                      <div key={l.COD} className="od-presupuesto-linea">
                        <div className="od-presupuesto-linea-txt">
                          <div className="od-presupuesto-linea-tit">
                            Pieza {l.NUMERO_PIEZA}{l.C_SUPERFICIE ? ` (${l.C_SUPERFICIE})` : ''} — {l.TRATAMIENTO_NOMBRE}
                          </div>
                          <div className="od-presupuesto-linea-sub">
                            ${Number(l.PRECIO).toFixed(2)}{l.DESCUENTO > 0 ? ` · -${l.DESCUENTO}%` : ''}
                            {l.C_ESTADO_PAGO === 'PAG' ? ' · Pagado' : l.C_ESTADO_PAGO === 'PAR' ? ' · Pago parcial' : ''}
                          </div>
                        </div>
                        {l.C_ESTADO_PAGO !== 'PAG' && (
                          <button className="od-presupuesto-linea-del" onClick={() => quitarLinea(l.COD)} title="Quitar">
                            <i className="ti ti-x" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <button className="btn-mo-sec" style={{ width: '100%', marginTop: 12 }}
                  onClick={() => {
                    if (!showAgregarPrestacion && piezaSel) {
                      setNuevaLinea(f => ({ ...f, NUMERO_PIEZA: piezaSel }));
                    }
                    setShowAgregarPrestacion(v => !v);
                  }}>
                  <i className="ti ti-plus" aria-hidden="true"></i> Agregar prestación
                </button>

                {showAgregarPrestacion && (
                  <div className="od-presupuesto-form">
                    <p className="od-form-hint">
                      Agrega un cargo al presupuesto de este paciente: elige la pieza, escribe qué se va a hacer y el precio.
                    </p>

                    <div className="od-form-row">
                      <div>
                        <div className="mo-sec">Pieza *</div>
                        <input className="form-control" placeholder="Ej: 18"
                          value={nuevaLinea.NUMERO_PIEZA}
                          onChange={e => setNuevaLinea(f => ({ ...f, NUMERO_PIEZA: e.target.value }))} />
                      </div>
                      <div>
                        <div className="mo-sec">Superficie</div>
                        <select className="form-control" value={nuevaLinea.C_SUPERFICIE}
                          onChange={e => setNuevaLinea(f => ({ ...f, C_SUPERFICIE: e.target.value }))}>
                          <option value="">Pieza completa</option>
                          {['M', 'D', 'V', 'L', 'O'].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    {piezaSel && (
                      <p className="od-form-tip">Pieza {piezaSel} tomada automáticamente de la que tienes abierta abajo.</p>
                    )}

                    <div className="mo-sec" style={{ marginTop: 10 }}>¿Qué se va a hacer? *</div>
                    <input className="form-control" list="od-catalogo-list" placeholder="Ej: Obturación resina compuesta"
                      value={nuevaLinea.NOMBRE_TRATAMIENTO}
                      onChange={e => {
                        const nombre = e.target.value;
                        const t = catalogo.find(c => c.NOMBRE.toLowerCase() === nombre.toLowerCase());
                        setNuevaLinea(f => ({ ...f, NOMBRE_TRATAMIENTO: nombre, PRECIO: t ? String(t.PRECIO_BASE) : f.PRECIO }));
                      }} />
                    <datalist id="od-catalogo-list">
                      {catalogo.map(c => <option key={c.COD} value={c.NOMBRE} />)}
                    </datalist>
                    {catalogo.length === 0 ? (
                      <p className="od-form-tip">Aún no tienes ninguna prestación guardada — escribe el nombre y el precio, y quedará guardada para la próxima vez.</p>
                    ) : (
                      <p className="od-form-tip">Empieza a escribir para elegir una ya guardada, o escribe una nueva.</p>
                    )}

                    <div className="mo-sec" style={{ marginTop: 10 }}>Precio *</div>
                    <input className="form-control" type="number" step="0.01" placeholder="0.00"
                      value={nuevaLinea.PRECIO}
                      onChange={e => setNuevaLinea(f => ({ ...f, PRECIO: e.target.value }))} />

                    {nuevaLinea.DESCUENTO > 0 || descuentoLineaAbierto ? (
                      <>
                        <div className="mo-sec" style={{ marginTop: 10 }}>Descuento en esta prestación (%)</div>
                        <input className="form-control" type="number" min="0" max="100"
                          value={nuevaLinea.DESCUENTO}
                          onChange={e => setNuevaLinea(f => ({ ...f, DESCUENTO: e.target.value }))} />
                      </>
                    ) : (
                      <button className="od-form-link" onClick={() => setDescuentoLineaAbierto(true)}>
                        + Agregar descuento a esta prestación
                      </button>
                    )}

                    <button className="btn-mo-pri" style={{ width: '100%', marginTop: 12 }} onClick={agregarLinea}>
                      Agregar al plan
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="od-odontograma-col">
            <div className="od-legend">
              <span><i className="od-dot" style={{ background: 'var(--text3)', opacity: .3 }}></i> Sana</span>
              <span><i className="od-dot" style={{ background: 'var(--red)' }}></i> Pendiente / problema</span>
              <span><i className="od-dot" style={{ background: 'var(--blue)' }}></i> Tratamiento completado</span>
              <span><i className="od-dot" style={{ border: '1px dashed var(--border)', background: 'transparent' }}></i> Ausente</span>
            </div>

            {loading ? <div className="spin-wrap"><div className="sp"/></div> : (
              <div className="od-arcada-wrap">
                <div className="od-arcada od-arcada-sup">
                  {[1, 2].map((cuad, ci) => (
                    <div key={cuad} className={`od-cuadrante ${ci === 0 ? 'od-cuad-der' : ''}`}>
                      {numerosFDI().filter(n => n[0] === String(cuad)).map(numero => {
                        const h = hallazgoDe(numero);
                        return (
                          <button key={numero}
                            className={`od-diente-btn ${piezaSel === numero ? 'sel' : ''}`}
                            onClick={() => abrirPieza(numero)}>
                            <DienteSVG estado={h?.estado} ausente={h?.estado === 'AUS'} />
                            {h?.tieneHallazgo && <span className="od-diente-flag" title="Tiene hallazgos por superficie" />}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>

                <div className="od-arcada-nums">
                  {[1, 2].flatMap(cuad => numerosFDI().filter(n => n[0] === String(cuad))).map(numero => (
                    <span key={numero} className="od-num-cell">{numero}</span>
                  ))}
                </div>
                <div className="od-arcada-linea"><span className="od-arcada-linea-c" /></div>
                <div className="od-arcada-nums">
                  {[4, 3].flatMap(cuad => numerosFDI().filter(n => n[0] === String(cuad))).map(numero => (
                    <span key={numero} className="od-num-cell">{numero}</span>
                  ))}
                </div>

                <div className="od-arcada od-arcada-inf">
                  {[4, 3].map((cuad, ci) => (
                    <div key={cuad} className={`od-cuadrante ${ci === 0 ? 'od-cuad-der' : ''}`}>
                      {numerosFDI().filter(n => n[0] === String(cuad)).map(numero => {
                        const h = hallazgoDe(numero);
                        return (
                          <button key={numero}
                            className={`od-diente-btn ${piezaSel === numero ? 'sel' : ''}`}
                            onClick={() => abrirPieza(numero)}>
                            <DienteSVG estado={h?.estado} ausente={h?.estado === 'AUS'} />
                            {h?.tieneHallazgo && <span className="od-diente-flag" title="Tiene hallazgos por superficie" />}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {piezaSel && (
              <div className="od-detalle">
                <div className="od-detalle-hdr">
                  <span>Pieza {piezaSel}</span>
                  <button className="btn-mo-sec" style={{ padding: '4px 10px' }} onClick={() => { setPiezaSel(null); setDetalle(null); }}>✕</button>
                </div>

                {loadDet ? <div className="spin-wrap"><div className="sp"/></div> : detalle && (
                  <div className="od-detalle-body">
                    <div className="od-detalle-col">
                      <div className="mo-sec">Estado general de la pieza</div>
                      <div className="od-chip-row">
                        {ESTADOS_PIEZA.map(e => (
                          <button key={e.cod} type="button"
                            className={`od-chip ${formPieza.C_ESTADO_PIEZA === e.cod ? 'act' : ''}`}
                            onClick={() => setFormPieza(f => ({ ...f, C_ESTADO_PIEZA: e.cod }))}>
                            {e.label}
                          </button>
                        ))}
                      </div>
                      <div className="mo-sec" style={{ marginTop: 10 }}>Tratamiento realizado *</div>
                      <input className="form-control" placeholder="Ej: Extracción simple pieza 18"
                        value={formPieza.TRATAMIENTO} onChange={e => setFormPieza(f => ({ ...f, TRATAMIENTO: e.target.value }))} />
                      <div className="mo-sec" style={{ marginTop: 10 }}>Observaciones</div>
                      <textarea className="form-control" rows={2}
                        value={formPieza.OBSERVACIONES} onChange={e => setFormPieza(f => ({ ...f, OBSERVACIONES: e.target.value }))} />
                      <button className="btn-mo-pri" style={{ marginTop: 10 }} disabled={saving} onClick={guardarPieza}>
                        {saving ? 'Guardando...' : 'Guardar estado de la pieza'}
                      </button>
                    </div>

                    <div className="od-detalle-col">
                      <div className="mo-sec">Superficies (clic para marcar hallazgo)</div>
                      <div className="od-cruz">
                        <button className="od-sup od-sup-v" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'V')?.estado) }}
                          onClick={() => abrirSuperficie('V')}>V</button>
                        <button className="od-sup od-sup-m" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'M')?.estado) }}
                          onClick={() => abrirSuperficie('M')}>M</button>
                        <div className="od-sup od-sup-centro">{piezaSel}</div>
                        <button className="od-sup od-sup-o" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'O')?.estado) }}
                          onClick={() => abrirSuperficie('O')}>O</button>
                        <button className="od-sup od-sup-d" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'D')?.estado) }}
                          onClick={() => abrirSuperficie('D')}>D</button>
                        <button className="od-sup od-sup-l" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'L')?.estado) }}
                          onClick={() => abrirSuperficie('L')}>L</button>
                      </div>

                      {superficieSel && (
                        <div className="od-sup-form">
                          <div className="mo-sec">Superficie {superficieSel}</div>
                          <div className="od-chip-row">
                            {ESTADOS_SUPERFICIE.map(e => (
                              <button key={e.cod} type="button"
                                className={`od-chip ${formSup.C_ESTADO_SUPERFICIE === e.cod ? 'act' : ''}`}
                                onClick={() => setFormSup(f => ({ ...f, C_ESTADO_SUPERFICIE: e.cod }))}>
                                {e.label}
                              </button>
                            ))}
                          </div>
                          <div className="mo-sec" style={{ marginTop: 10 }}>Tratamiento realizado *</div>
                          <input className="form-control" placeholder="Ej: Obturación resina compuesta"
                            value={formSup.TRATAMIENTO} onChange={e => setFormSup(f => ({ ...f, TRATAMIENTO: e.target.value }))} />
                          <div className="mo-sec" style={{ marginTop: 10 }}>Observaciones</div>
                          <textarea className="form-control" rows={2}
                            value={formSup.OBSERVACIONES} onChange={e => setFormSup(f => ({ ...f, OBSERVACIONES: e.target.value }))} />
                          <button className="btn-mo-pri" style={{ marginTop: 10 }} disabled={saving} onClick={guardarSuperficie}>
                            {saving ? 'Guardando...' : 'Guardar superficie'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        )}

        {tab === 'historial' && (
          <div className="od-body">
            {historial.length === 0 ? (
              <div className="empty-hc">Sin tratamientos registrados</div>
            ) : (
              <div className="od-hist-list">
                {historial.map(h => (
                  <div key={h.COD} className="od-hist-row">
                    <div className="od-hist-fecha">{h.FECHA?.slice(0,10)}</div>
                    <div className="od-hist-body">
                      <div className="od-hist-tratamiento">Pieza {h.NUMERO_PIEZA}{h.C_SUPERFICIE ? ` · Sup. ${h.C_SUPERFICIE}` : ''} — {h.TRATAMIENTO}</div>
                      <div className="od-hist-sub">{h.USUARIO_NOMBRE || h.USUARIO_REGISTRO} · Estado resultante: {h.C_ESTADO_RESULTANTE}</div>
                      {h.OBSERVACIONES && <div className="od-hist-obs">{h.OBSERVACIONES}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

const css = `
.od-search-wrap { max-width:440px; margin:60px auto; text-align:center; }
.od-search-title { font-size:16px; font-weight:700; color:var(--text); margin-bottom:16px; }
.od-search-input { width:100%; padding:10px 14px; border:1px solid var(--border); border-radius:9px; font-size:14px; background:var(--surface); color:var(--text); outline:none; }
.od-search-input:focus { border-color:var(--navy-lt); }
.od-search-sugs { margin-top:8px; border:1px solid var(--border); border-radius:9px; overflow:hidden; text-align:left; background:var(--surface); }
.od-search-sug { padding:9px 14px; cursor:pointer; border-bottom:1px solid var(--bg); }
.od-search-sug:hover { background:var(--bg); }
.od-search-sug-nom { font-size:13px; font-weight:600; color:var(--text); }
.od-search-sug-ci { font-size:11px; color:var(--text3); }

.od-msg { padding:8px 14px; border-radius:8px; font-size:12px; margin-bottom:8px; }
.od-msg.ok  { background:var(--green-lt); color:var(--green); border:1px solid var(--green); }
.od-msg.err { background:var(--red-lt);   color:var(--red);   border:1px solid var(--red); }

.od-page { display:flex; flex-direction:column; gap:12px; }
.od-hdr  { display:flex; align-items:center; justify-content:space-between; background:var(--navy); color:#fff; padding:14px 18px; border-radius:12px; }
.od-hdr-name { font-size:15px; font-weight:700; }
.od-hdr-sub  { font-size:12px; opacity:.8; margin-top:2px; }

.od-tabs { display:flex; gap:4px; border-bottom:1px solid var(--border); }
.od-tab  { padding:9px 16px; font-size:13px; cursor:pointer; color:var(--text3); border-bottom:2px solid transparent; display:flex; align-items:center; gap:6px; }
.od-tab.act { color:var(--navy); border-bottom-color:var(--navy-lt); font-weight:600; }

.od-body { display:flex; flex-direction:column; gap:14px; }
.od-body-conpresupuesto { display:grid; grid-template-columns:260px 1fr; align-items:start; gap:16px; }
@media (max-width:860px) { .od-body-conpresupuesto { grid-template-columns:1fr; } }
.od-odontograma-col { display:flex; flex-direction:column; gap:14px; min-width:0; }

.od-presupuesto { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:16px; }
.od-presupuesto-hdr { font-size:11px; color:var(--text3); text-transform:uppercase; letter-spacing:.3px; }
.od-presupuesto-total { font-size:26px; font-weight:700; color:var(--navy); margin:2px 0 12px; }
.od-presupuesto-desc { display:flex; align-items:center; justify-content:space-between; font-size:12px; color:var(--text2); padding-top:10px; border-top:1px solid var(--border); }
.od-presupuesto-desc-edit { display:flex; align-items:center; gap:4px; }
.od-presupuesto-desc-edit input { width:44px; padding:3px 5px; font-size:11px; border:1px solid var(--border); border-radius:6px; background:var(--bg); color:var(--text); }
.od-presupuesto-lineas { display:flex; flex-direction:column; gap:8px; margin-top:12px; padding-top:12px; border-top:1px solid var(--border); }
.od-presupuesto-linea { display:flex; align-items:flex-start; justify-content:space-between; gap:6px; }
.od-presupuesto-linea-tit { font-size:11.5px; font-weight:600; color:var(--text); line-height:1.3; }
.od-presupuesto-linea-sub { font-size:10.5px; color:var(--text3); margin-top:1px; }
.od-presupuesto-linea-del { background:none; border:none; color:var(--text3); cursor:pointer; padding:2px; flex-shrink:0; }
.od-presupuesto-linea-del:hover { color:var(--red); }
.od-presupuesto-form { margin-top:12px; padding-top:12px; border-top:1px solid var(--border); }
.od-form-hint { font-size:11.5px; color:var(--text2); line-height:1.4; margin:0 0 10px; }
.od-form-tip { font-size:10.5px; color:var(--text3); margin:4px 0 0; line-height:1.4; }
.od-form-row { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.od-form-link { background:none; border:none; color:var(--navy-lt); font-size:11.5px; cursor:pointer; padding:6px 0; text-align:left; }
.od-form-link:hover { text-decoration:underline; }
.od-legend { display:flex; gap:16px; flex-wrap:wrap; font-size:11px; color:var(--text2); }
.od-dot { display:inline-block; width:10px; height:10px; border-radius:50%; margin-right:5px; vertical-align:-1px; }

.od-arcada-wrap { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:22px 18px; overflow-x:auto; }
.od-arcada { display:flex; justify-content:center; gap:0; }
.od-cuadrante { display:flex; gap:0; padding:0 5px; }
.od-cuad-der { border-right:1.5px solid var(--border); }
.od-diente-btn { position:relative; width:32px; display:flex; align-items:flex-end; justify-content:center; background:none; border:1px solid transparent; border-radius:6px; padding:4px 0; cursor:pointer; }
.od-arcada-sup .od-diente-btn { align-items:flex-end; }
.od-arcada-inf .od-diente-btn { align-items:flex-start; }
.od-diente-btn:hover { background:var(--bg); }
.od-diente-btn.sel { border-color:var(--navy-lt); background:var(--blue-lt); }
.od-diente-flag { position:absolute; top:2px; right:1px; width:6px; height:6px; border-radius:50%; background:var(--amber); }
.od-arcada-nums { display:flex; justify-content:center; }
.od-num-cell { flex-shrink:0; width:32px; text-align:center; font-size:10px; font-weight:600; color:var(--text2); padding:2px 0; }
.od-arcada-linea { display:flex; align-items:center; padding:1px 0; }
.od-arcada-linea-c { display:block; width:100%; height:1px; background:var(--border); }

.od-detalle { border:1px solid var(--border); border-radius:12px; overflow:hidden; }
.od-detalle-hdr { padding:9px 14px; background:var(--surface2); border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; font-size:13px; font-weight:700; color:var(--text); }
.od-detalle-body { display:grid; grid-template-columns:1fr 1fr; gap:16px; padding:16px; }
@media (max-width:720px) { .od-detalle-body { grid-template-columns:1fr; } }
.od-detalle-col { display:flex; flex-direction:column; }

.od-chip-row { display:flex; flex-wrap:wrap; gap:6px; }
.od-chip { padding:5px 11px; border-radius:20px; border:1px solid var(--border); background:var(--bg); font-size:11px; cursor:pointer; color:var(--text2); }
.od-chip.act { background:var(--navy); border-color:var(--navy); color:#fff; }

.od-cruz { position:relative; width:180px; height:180px; margin:10px auto; }
.od-sup { position:absolute; border:1px solid var(--border); cursor:pointer; font-size:11px; font-weight:700; color:var(--text); display:flex; align-items:center; justify-content:center; }
.od-sup-v { top:0; left:50px; width:80px; height:50px; border-radius:8px 8px 0 0; }
.od-sup-m { left:0; top:50px; width:50px; height:80px; border-radius:8px 0 0 8px; }
.od-sup-d { right:0; top:50px; width:50px; height:80px; border-radius:0 8px 8px 0; }
.od-sup-l { bottom:0; left:50px; width:80px; height:50px; border-radius:0 0 8px 8px; }
.od-sup-o { left:50px; top:50px; width:80px; height:80px; }
.od-sup-centro { position:absolute; left:50px; top:50px; width:80px; height:80px; display:flex; align-items:center; justify-content:center; font-size:10px; color:var(--text3); pointer-events:none; }
.od-sup-form { margin-top:12px; padding-top:12px; border-top:1px solid var(--border); }

.od-hist-list { display:flex; flex-direction:column; gap:8px; }
.od-hist-row { display:flex; gap:12px; padding:10px 14px; border:1px solid var(--border); border-radius:9px; background:var(--surface); }
.od-hist-fecha { font-size:11px; color:var(--text3); flex-shrink:0; width:70px; }
.od-hist-tratamiento { font-size:13px; font-weight:600; color:var(--text); }
.od-hist-sub { font-size:11px; color:var(--text3); margin-top:2px; }
.od-hist-obs { font-size:11px; color:var(--text2); margin-top:4px; font-style:italic; }
`;
